import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { ConnectableApp } from './hooks/useConnectableApps.ts';

// `BylineConnectionIconStack`'s treatment, replicated for the same reason
// `ShareContextLogoPile` does: the stack's bordered tile doubles the border on 3P logos
// that already carry their own.
const styles = cssMap({
	pile: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		isolation: 'isolate',
		// Reads as not-connected-yet.
		opacity: token('opacity.disabled'),
	},
	firstTile: {
		display: 'inline-flex',
		flexShrink: 0,
		zIndex: 300,
	},
	middleTile: {
		display: 'inline-flex',
		flexShrink: 0,
		marginInlineStart: token('space.negative.050'),
		zIndex: 200,
		rotate: '6deg',
	},
	lastTile: {
		display: 'inline-flex',
		flexShrink: 0,
		marginInlineStart: token('space.negative.050'),
		zIndex: 100,
	},
});

const tileStyles = [styles.firstTile, styles.middleTile, styles.lastTile];

export type GhostConnectorIconsProps = {
	apps: ConnectableApp[];
};

/**
 * Hidden from assistive tech: the copy never names these apps, so announcing them adds
 * nothing. The wrapper still renders with no apps — it is the leading half of the banner's
 * `space-between` row, and dropping it would shift the arrow while the apps resolve.
 */
export const GhostConnectorIcons = ({ apps }: GhostConnectorIconsProps): React.JSX.Element => (
	<Box aria-hidden="true" testId="teamwork-graph-promo-banner-ghost-icons" xcss={styles.pile}>
		{apps.map(({ productKey, id, Icon }, index) => (
			<Box
				key={productKey}
				xcss={tileStyles[index] ?? styles.lastTile}
				testId={`teamwork-graph-promo-banner-ghost-icon-${id}`}
			>
				<Icon size="xsmall" label="" />
			</Box>
		))}
	</Box>
);
